/**
 * Desktop Friends — roster + admin panel (lane D1; INVENTORY-DESKTOP §3.9,
 * prototype template lines 619-697).
 *
 * Reached from the top nav (`FRIENDS`), never from Profile — mobile's `FRIENDS`
 * row on the profile page is GONE on desktop (§3.10).
 *
 * No props: every value comes from `crewVM()` and every write is a crew- or
 * ui-slice action, exactly as on mobile. Two behaviours look like screen state
 * and are not:
 *
 *  - **REMOVE is two-tap.** `rmArm` lives in the ui slice, so navigating away
 *    disarms it (`nav()` clears it) and the armed label/colour arrive
 *    pre-resolved as `rmTxt` / `rmBd` / `rmClr`.
 *  - **There is no role control.** Roles follow who owns the crew (D4), the
 *    design removed the select with CO-ADMIN, and phase B retired the endpoint
 *    behind it — so the row shows a read-only OWNER / PLAYER chip. Changing who
 *    owns a crew is TRANSFER OWNERSHIP, not a dropdown.
 *
 * DESKTOP LAYOUT (§1.3): page `max-width:1180px; padding:30px 26px 40px;
 * gap:20px`; roster `repeat(auto-fill,minmax(240px,1fr))` with 52px avatars
 * (mobile: one card per row, 48px); the add-user form is ONE row; admin rows are
 * horizontal (avatar · name/email · role · status · spacer · actions) instead of
 * mobile's two stacked lines.
 *
 * DEVIATIONS
 *  - §3.9 / §9.3: the prototype's `statusTxt` reads `TEMPORARY PASSWWORD`
 *    (three W's, line 2433). `crewVM` ships the correct `TEMP PASSWORD` and we
 *    keep the app string.
 *  - D-03 (inherited from mobile): rows whose copy button reads `COPY INVITE`
 *    carry `INVITE_CODE_NOTE` on their own wrapped line. The prototype has no
 *    such note because it re-derived the temp code on demand; we cannot.
 *  - P2-08 (inherited) is CLOSED by migration 3010: the dots are real for the
 *    whole crew, not just your own card. The prototype hardcoded
 *    `['marcus','chris','chelsea']`; this asks `crew_steam_links` and blanks the
 *    chip until it answers.
 */

import { useEffect } from 'react';

import { Avatar } from '../../components/Avatar.tsx';
import { useQd } from '../../store/index.ts';
import { crewVM } from '../../store/selectors.ts';

const MONO = 'var(--gt-font-mono)';
const PIXEL = 'var(--gt-font-pixel)';

/** Admin-row action chips (template lines 685-687). */
export default function Crew(): JSX.Element {
  const s = useQd();
  const v = crewVM(s);

  useEffect(() => {
    if (v.meAdmin) {
      void s.loadPending();
      // 3056: the join link and its requests load on the same trigger and the
      // same owner check — three reads for one screen, all of which swallow
      // their own failures rather than blanking the section.
      void s.loadJoinLink();
      void s.loadJoinRequests();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [v.meAdmin]);

  return (
    <div
      data-screen-label="Party"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
        padding: '30px 26px 40px',
        maxWidth: '1180px',
        width: '100%',
        margin: '0 auto',
        boxSizing: 'border-box',
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        <div style={{ fontFamily: 'var(--gt-font-pixel)', fontSize: '13px', color: 'var(--gt-text)' }}>PARTY</div>
        <div style={{ fontFamily: MONO, fontSize: '10.5px', color: 'var(--gt-muted)' }}>
          <span>{v.crewSub}</span>
        </div>
      </div>

      {/* MANAGE PARTY (AA phase 5 §K): everything administrative lives behind
          this one row, leaders only. The guard is on the row AND on the screen. */}
      {v.meAdmin ? (
        <div
          onClick={() => s.nav('manage')}
          data-tip="ROSTER · INVITES · RENAME · HAND OVER · DISBAND"
          className="d-hov-acc-bd"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            height: 52,
            padding: '0 16px',
            border: '1px solid var(--gt-border)',
            background: 'var(--gt-card)',
            cursor: 'pointer',
            boxSizing: 'border-box',
          }}
        >
          <span style={{ fontFamily: MONO, fontSize: 13, color: 'var(--acc)' }}>◈</span>
          <span style={{ fontFamily: PIXEL, fontSize: 9, color: 'var(--gt-text)', transform: 'translateY(0.13em)' }}>MANAGE PARTY</span>
          {v.hasJqWait ? (
            <span style={{ fontFamily: MONO, fontSize: 8.5, letterSpacing: '.08em', color: 'var(--acc)', border: '1px solid var(--acc)', padding: '3px 6px' }}>{v.jqWaitTxt}</span>
          ) : null}
          <div style={{ flex: 1 }} />
          <span style={{ fontFamily: MONO, fontSize: 11, color: 'var(--gt-text-5)' }}>▸</span>
        </div>
      ) : null}

      {/* roster — auto-fill minmax(240px,1fr): 4 columns at 1280 (§1.4 item 7) */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(240px,1fr))', gap: '14px' }}>
        {v.crewCards.map((u) => (
          <div
            key={u.key}
            onClick={() => s.openMember(u.k)}
            className="d-hov-acc-bd"
            style={{
              cursor: 'pointer',
              display: 'flex',
              gap: '14px',
              alignItems: 'center',
              border: '1px solid var(--gt-line)',
              background: 'var(--gt-card)',
              padding: '14px',
            }}
          >
            <Avatar size={52} fs={13} ini={u.ini} bg={u.hueBg} hasAv={u.hasAv} avBg={u.avBg} />
            <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '3px' }}>
              <div style={{ display: 'flex', gap: '7px', alignItems: 'baseline' }}>
                <span style={{ fontWeight: 600, fontSize: '14px', color: 'var(--gt-text)' }}>{u.name}</span>
                {u.isYou ? (
                  <span
                    style={{
                      fontFamily: 'var(--gt-font-pixel)',
                      fontSize: '6px',
                      background: 'var(--gt-text)',
                      color: 'var(--gt-ink)',
                      padding: '3px 4px',
                    }}
                  >
                    YOU
                  </span>
                ) : null}
              </div>
              <div style={{ fontFamily: MONO, fontSize: '9.5px', color: u.roleClr }}>
                <span>{u.roleTxt}</span>
                {u.isTemp ? <span style={{ color: 'var(--acc2)' }}> · TEMP PASS</span> : null}
              </div>
              <div style={{ fontFamily: MONO, fontSize: '9.5px', color: 'var(--gt-muted-2)' }}>
                <span>{u.statsTxt}</span>
              </div>
              {/* Blank until `crew_steam_links` answers — same rule as the
                  detail panel's library column. Your own card never waits. */}
              {u.stTxt ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{ width: '6px', height: '6px', flex: 'none', background: u.stDot }} />
                  <span style={{ fontFamily: MONO, fontSize: '9px', letterSpacing: '.03em', color: u.stClr }}>
                    {u.stTxt}
                  </span>
                </div>
              ) : null}
            </div>
          </div>
        ))}
      </div>

      {v.notAdmin ? (
        <div
          style={{
            border: '1px dashed var(--gt-border-2)',
            padding: '12px 14px',
            fontFamily: MONO,
            fontSize: '10.5px',
            color: 'var(--gt-muted)',
          }}
        >
          {v.askAdmTxt}
        </div>
      ) : null}

    </div>
  );
}

